import { makeAutoObservable } from 'mobx'
import { CDN_FRONT } from 'shared/config'
import type { IEntitiesStore } from 'entities/EntitiesExport'

const DOMAIN_VALID_ERROR_MESSAGE = 'Enter a valid domain'

const EMBED_SCRIPT_START = '<!-- Start of Salesmsg Embed Code -->'
const EMBED_SCRIPT_END = '<!-- End of Salesmsg Embed Code -->'

function buildEmbedScript(cdnUrl: string, hashId: string): string {
  const scriptLine = `<script type="text/javascript" src="${cdnUrl}/js/combine-${hashId}.js"></script>`

  return [EMBED_SCRIPT_START, scriptLine, EMBED_SCRIPT_END].join('\n')
}

function isValidDomainOrUrl(value: string): boolean {
  const trimmed = value.trim()

  if (!trimmed) return false

  try {
    const url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
    const hostname = url.hostname
    if (!hostname) return false
    if (hostname.includes('.')) return true
    return false
  } catch {
    return false
  }
}

export class InstallBlockStore {
  domains: string[] = []
  domainErrors: (string | null)[] = []

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  syncFromApi(domains: string[]) {
    this.domains = domains.length > 0 ? [...domains] : []
    this.domainErrors = this.domains.map(() => null)
  }

  setDomains = (domains: string[]) => {
    this.domains = domains
    this.domainErrors = this.domains.map(() => null)
  }

  setDomainAt = (index: number, value: string) => {
    const next = [...this.domains]
    if (index >= next.length) {
      const oldLength = next.length
      next.length = index + 1
      next.fill('', oldLength, index)
    }
    next[index] = value
    this.domains = next
    const errorsNext = [...this.domainErrors]
    if (index >= errorsNext.length) errorsNext.length = index + 1
    errorsNext[index] = null
    this.domainErrors = errorsNext
  }

  validateDomainAt = (index: number) => {
    const value = this.domains[index] ?? ''
    const trimmed = value.trim()
    if (!trimmed) {
      this.setDomainErrorAt(index, DOMAIN_VALID_ERROR_MESSAGE)
      return false
    }
    if (!isValidDomainOrUrl(trimmed)) {
      this.setDomainErrorAt(index, DOMAIN_VALID_ERROR_MESSAGE)
      return false
    }
    this.setDomainErrorAt(index, null)
    return true
  }

  private setDomainErrorAt(index: number, error: string | null) {
    const next = [...this.domainErrors]
    if (index >= next.length) next.length = index + 1
    next[index] = error
    this.domainErrors = next
  }

  getDomainErrorAt = (index: number): string | null => {
    return this.domainErrors?.[index] ?? null
  }

  validate(): boolean {
    const list = this.displayList
    let allValid = true
    for (let i = 0; i < list.length; i++) {
      const valid = this.validateDomainAt(i)
      if (!valid) allValid = false
    }
    return allValid
  }

  setDomainErrorsForIndexes = (invalidIndexes: number[]) => {
    if (invalidIndexes.length === 0) return

    const next = [...this.domainErrors]

    for (const index of invalidIndexes) {
      if (index < 0) continue
      if (index >= next.length) next.length = index + 1
      next[index] = DOMAIN_VALID_ERROR_MESSAGE
    }

    this.domainErrors = next
  }

  addDomainSlot = () => {
    this.domains = [...this.domains, '']
    this.domainErrors = [...this.domainErrors, null]
  }

  removeDomainAt = (index: number) => {
    if (index < 0 || index >= this.domains.length) return
    this.domains = this.domains.filter((_, i) => i !== index)
    this.domainErrors = this.domainErrors.filter((_, i) => i !== index)
  }

  get displayList(): string[] {
    return this.domains.length > 0 ? this.domains : ['']
  }

  get scriptSnippet(): string {
    const hashId = this._entities.organizationStore.hashId ?? ''
    const cdnUrl = (CDN_FRONT || '').replace(/\/$/, '')

    if (!hashId || !cdnUrl) return ''

    return buildEmbedScript(`https://${cdnUrl}`, hashId)
  }

  getSnapshot(): { domains: string[] } {
    const nonEmpty = this.domains.filter((d) => d.trim())

    return { domains: nonEmpty }
  }
}
